import type { CitedItem, MomAction, MomQuote } from '@sl/shared';
import { useTranslation } from 'react-i18next';

/**
 * The building blocks of the structured MOM editor. Deliberately plain: the
 * reviewer edits fields, never a blank page (scope §8.4), and every item that
 * came off the call keeps its citation chips next to it so a claim and its
 * evidence are never more than a click apart.
 */

export interface FieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  rows?: number;
}

export function TextField({ label, value, onChange, disabled }: FieldProps): JSX.Element {
  return (
    <label className="mom-field">
      <span className="mom-field-label">{label}</span>
      <input dir="auto" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

export function TextAreaField({ label, value, onChange, disabled, rows }: FieldProps): JSX.Element {
  return (
    <label className="mom-field">
      <span className="mom-field-label">{label}</span>
      <textarea
        dir="auto"
        rows={rows ?? 4}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

/** Up / down / remove, shared by every list editor. */
function RowControls({
  index,
  length,
  disabled,
  onMove,
  onRemove,
}: {
  index: number;
  length: number;
  disabled?: boolean;
  onMove: (from: number, to: number) => void;
  onRemove: (index: number) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="mom-row-controls">
      <button
        type="button"
        className="link"
        disabled={disabled || index === 0}
        title={t('mom.moveUp') as string}
        onClick={() => onMove(index, index - 1)}
      >
        ↑
      </button>
      <button
        type="button"
        className="link"
        disabled={disabled || index === length - 1}
        title={t('mom.moveDown') as string}
        onClick={() => onMove(index, index + 1)}
      >
        ↓
      </button>
      <button
        type="button"
        className="link danger"
        disabled={disabled}
        title={t('mom.remove') as string}
        onClick={() => onRemove(index)}
      >
        ×
      </button>
    </div>
  );
}

export function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}

export function CitationChips({
  segmentIds,
  onCite,
}: {
  segmentIds: number[];
  onCite: (idx: number) => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  if (segmentIds.length === 0) return null;
  return (
    <span className="mom-chips">
      {segmentIds.map((idx) => (
        <button
          key={idx}
          type="button"
          className="mom-chip"
          title={t('mom.chipTitle') as string}
          onClick={() => onCite(idx)}
        >
          #{idx}
        </button>
      ))}
    </span>
  );
}

export function StringListEditor({
  label,
  items,
  disabled,
  onChange,
}: {
  label: string;
  items: string[];
  disabled?: boolean;
  onChange: (items: string[]) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="mom-list">
      <div className="mom-field-label">{label}</div>
      {items.map((item, i) => (
        <div className="mom-list-row" key={i}>
          <textarea
            dir="auto"
            rows={2}
            value={item}
            disabled={disabled}
            onChange={(e) => onChange(items.map((v, j) => (j === i ? e.target.value : v)))}
          />
          <RowControls
            index={i}
            length={items.length}
            disabled={disabled}
            onMove={(from, to) => onChange(move(items, from, to))}
            onRemove={(index) => onChange(items.filter((_, j) => j !== index))}
          />
        </div>
      ))}
      <button
        type="button"
        className="secondary small"
        disabled={disabled}
        onClick={() => onChange([...items, ''])}
      >
        {t('mom.add')}
      </button>
    </div>
  );
}

export function CitedListEditor({
  label,
  items,
  disabled,
  onChange,
  onCite,
}: {
  label: string;
  items: CitedItem[];
  disabled?: boolean;
  onChange: (items: CitedItem[]) => void;
  onCite: (idx: number) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const set = (i: number, patch: Partial<CitedItem>): void =>
    onChange(items.map((v, j) => (j === i ? { ...v, ...patch } : v)));
  return (
    <div className="mom-list">
      <div className="mom-field-label">{label}</div>
      {items.map((item, i) => (
        <div className="mom-list-row" key={i}>
          <div className="mom-list-main">
            <textarea
              dir="auto"
              rows={2}
              value={item.text}
              disabled={disabled}
              onChange={(e) => set(i, { text: e.target.value })}
            />
            <div className="mom-list-meta">
              <CitationChips segmentIds={item.segmentIds} onCite={onCite} />
              <label className="mom-inline-check">
                <input
                  type="checkbox"
                  checked={item.processStep}
                  disabled={disabled}
                  onChange={(e) => set(i, { processStep: e.target.checked })}
                />
                {t('mom.processStep')}
              </label>
              {item.segmentIds.length === 0 && !item.processStep && (
                <span className="mom-uncited">{t('mom.uncited')}</span>
              )}
            </div>
          </div>
          <RowControls
            index={i}
            length={items.length}
            disabled={disabled}
            onMove={(from, to) => onChange(move(items, from, to))}
            onRemove={(index) => onChange(items.filter((_, j) => j !== index))}
          />
        </div>
      ))}
      <button
        type="button"
        className="secondary small"
        disabled={disabled}
        onClick={() => onChange([...items, { text: '', segmentIds: [], processStep: false }])}
      >
        {t('mom.add')}
      </button>
    </div>
  );
}

export function ActionsEditor({
  items,
  disabled,
  onChange,
  onCite,
}: {
  items: MomAction[];
  disabled?: boolean;
  onChange: (items: MomAction[]) => void;
  onCite: (idx: number) => void;
}): JSX.Element {
  const { t } = useTranslation();
  // Row numbers are display order, so they are recomputed after every move or
  // removal rather than carried around as data the reviewer could contradict.
  const renumber = (list: MomAction[]): MomAction[] => list.map((a, i) => ({ ...a, n: i + 1 }));
  const set = (i: number, patch: Partial<MomAction>): void =>
    onChange(items.map((v, j) => (j === i ? { ...v, ...patch } : v)));
  return (
    <div className="mom-list">
      <div className="mom-field-label">{t('mom.sections.actions')}</div>
      {items.map((a, i) => (
        <div className="mom-action-row" key={i}>
          <div className="mom-action-n">{a.n}</div>
          <div className="mom-list-main">
            <textarea
              dir="auto"
              rows={2}
              value={a.action}
              disabled={disabled}
              onChange={(e) => set(i, { action: e.target.value })}
            />
            <div className="mom-action-fields">
              <input
                dir="auto"
                placeholder={t('mom.owner') as string}
                value={a.owner}
                disabled={disabled}
                onChange={(e) => set(i, { owner: e.target.value })}
              />
              <input
                dir="auto"
                placeholder={t('mom.department') as string}
                value={a.department}
                disabled={disabled}
                onChange={(e) => set(i, { department: e.target.value })}
              />
              <input
                dir="auto"
                placeholder={t('mom.due') as string}
                value={a.due}
                disabled={disabled}
                onChange={(e) => set(i, { due: e.target.value })}
              />
              <input
                dir="auto"
                placeholder={t('mom.status') as string}
                value={a.status}
                disabled={disabled}
                onChange={(e) => set(i, { status: e.target.value })}
              />
            </div>
            <div className="mom-list-meta">
              <CitationChips segmentIds={a.segmentIds} onCite={onCite} />
              <label className="mom-inline-check">
                <input
                  type="checkbox"
                  checked={a.processStep}
                  disabled={disabled}
                  onChange={(e) => set(i, { processStep: e.target.checked })}
                />
                {t('mom.processStep')}
              </label>
            </div>
          </div>
          <RowControls
            index={i}
            length={items.length}
            disabled={disabled}
            onMove={(from, to) => onChange(renumber(move(items, from, to)))}
            onRemove={(index) => onChange(renumber(items.filter((_, j) => j !== index)))}
          />
        </div>
      ))}
      <button
        type="button"
        className="secondary small"
        disabled={disabled}
        onClick={() =>
          onChange(
            renumber([
              ...items,
              {
                n: items.length + 1,
                action: '',
                owner: '',
                department: '',
                due: '',
                status: 'Open',
                segmentIds: [],
                processStep: false,
              },
            ]),
          )
        }
      >
        {t('mom.add')}
      </button>
    </div>
  );
}

export function QuotesEditor({
  items,
  disabled,
  onChange,
  onCite,
}: {
  items: MomQuote[];
  disabled?: boolean;
  onChange: (items: MomQuote[]) => void;
  onCite: (idx: number) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const set = (i: number, patch: Partial<MomQuote>): void =>
    onChange(items.map((v, j) => (j === i ? { ...v, ...patch } : v)));
  return (
    <div className="mom-list">
      <div className="mom-field-label">{t('mom.sections.inTheClientsWords')}</div>
      <p className="muted small">{t('mom.quotesHint')}</p>
      {items.map((q, i) => (
        <div className="mom-list-row" key={i}>
          <div className="mom-list-main">
            <textarea
              dir="auto"
              rows={2}
              value={q.quote}
              disabled={disabled}
              onChange={(e) => set(i, { quote: e.target.value })}
            />
            <div className="mom-list-meta">
              <input
                className="mom-speaker"
                dir="auto"
                placeholder={t('mom.speaker') as string}
                value={q.speaker}
                disabled={disabled}
                onChange={(e) => set(i, { speaker: e.target.value })}
              />
              <CitationChips segmentIds={q.segmentIds} onCite={onCite} />
            </div>
          </div>
          <RowControls
            index={i}
            length={items.length}
            disabled={disabled}
            onMove={(from, to) => onChange(move(items, from, to))}
            onRemove={(index) => onChange(items.filter((_, j) => j !== index))}
          />
        </div>
      ))}
      <button
        type="button"
        className="secondary small"
        disabled={disabled}
        onClick={() => onChange([...items, { quote: '', speaker: '', segmentIds: [] }])}
      >
        {t('mom.add')}
      </button>
    </div>
  );
}
